import { useEffect, useState } from 'react';

type DeviceCategory = 'mobile' | 'tablet' | 'desktop';

interface DeviceInfo {
    category: DeviceCategory;
    isMobile: boolean;
    isTablet: boolean;
    isDesktop: boolean;
    hasTouch: boolean;
    hasFinePointer: boolean;
    screenWidth: number;
    isHydrated: boolean;
    isPortrait: boolean;
    isMobileDevice: boolean;
    isTabletDevice: boolean;
    isDesktopDevice: boolean;
}

function detectLayoutCategory(): DeviceCategory {
    if (typeof window === 'undefined') return 'desktop';

    const width = window.innerWidth;
    const hasFinePointer = window.matchMedia('(pointer: fine)').matches;
    const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // Chrome DevToolsエミュレーター判定
    // (fine + coarse + touch が全部trueは開発環境の可能性が高い)
    const likelyDevTools = hasFinePointer && hasCoarsePointer && hasTouch;

    // モバイル: 768px未満
    if (width < 768) {
        return 'mobile';
    }

    // タブレット判定の優先順位:
    // 1. DevToolsエミュレーター → 画面幅で判定
    if (likelyDevTools) {
        if (width < 1024) return 'tablet';
        return 'desktop';
    }

    // 2. 実デバイス: タッチあり + 精密ポインターなし → タブレット
    if (hasTouch && !hasFinePointer) {
        // ここに来るのは: iPad, Androidタブレット（マウスなし）
        return width < 1280 ? 'tablet' : 'desktop'; // 大画面タブレット考慮
    }

    // 3. タブレット + マウス接続
    if (hasTouch && hasFinePointer && width < 1280) {
        return 'tablet'; // マウス付きタブレットもtablet扱い
    }

    // 4. それ以外（PC、タッチ対応ノートPC）
    return 'desktop';
}

function detectHardwareCategory(): DeviceCategory {
    if (typeof window === 'undefined') return 'desktop';

    // ブラウザのウィンドウサイズではなく、デバイス自体の物理的な画面解像度を取得
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;

    // 向き（縦・横）に依存しないよう、短い辺と長い辺を算出
    const screenShortEdge = Math.min(screenWidth, screenHeight);
    const screenLongEdge = Math.max(screenWidth, screenHeight);

    const hasFinePointer = window.matchMedia('(pointer: fine)').matches;
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // モバイル: 600px未満
    if (screenShortEdge < 600) return 'mobile';

    // タッチ操作可能 ＋ マウス等の精密ポインターなし → タブレット
    if (hasTouch && !hasFinePointer) return 'tablet';

    // タッチ操作可能 ＋ 精密ポインターあり ＋ 長辺が1366px以下 → タブレット
    if (hasTouch && hasFinePointer && screenLongEdge <= 1366) return 'tablet';

    // 4. それ以外（PC、タッチ対応ノートPC、大型モニター）
    return 'desktop';
}

function getInitialDeviceInfo(): DeviceInfo {
    return {
        category: 'desktop',
        isMobile: false,
        isTablet: false,
        isDesktop: true,
        hasTouch: false,
        hasFinePointer: true,
        screenWidth: 1920,
        isHydrated: false,
        isPortrait: false,
        isMobileDevice: false,
        isTabletDevice: false,
        isDesktopDevice: true,
    };
}

export function useDeviceType(): DeviceInfo {
    const [info, setInfo] = useState<DeviceInfo>(getInitialDeviceInfo);

    useEffect(() => {
        const updateDeviceInfo = () => {
            const category = detectLayoutCategory();
            const hardwareCategory = detectHardwareCategory();
            const currentIsPortrait = window.innerHeight >= window.innerWidth;
            setInfo({
                category,
                isMobile: category === 'mobile',
                isTablet: category === 'tablet',
                isDesktop: category === 'desktop',
                hasTouch: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
                hasFinePointer: window.matchMedia('(pointer: fine)').matches,
                screenWidth: window.innerWidth,
                isPortrait: currentIsPortrait,
                isHydrated: true,
                isMobileDevice: hardwareCategory === 'mobile',
                isTabletDevice: hardwareCategory === 'tablet',
                isDesktopDevice: hardwareCategory === 'desktop',
            });
        };

        updateDeviceInfo();

        let timeoutId: NodeJS.Timeout;
        const handleResize = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(updateDeviceInfo, 150);
        };

        window.addEventListener('resize', handleResize);

        const finePointerQuery = window.matchMedia('(pointer: fine)');
        finePointerQuery.addEventListener('change', updateDeviceInfo);

        return () => {
            window.removeEventListener('resize', handleResize);
            finePointerQuery.removeEventListener('change', updateDeviceInfo);
            clearTimeout(timeoutId);
        };
    }, []);

    return info;
}
